import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { mcpServersClient } from '@terra/shared-rest-clients-mcp-servers';
import type {
  CreateMcpServerDto,
  UpdateMcpServerDto,
  UpsertMcpCredentialDto,
} from '@terra/shared-contracts-mcp-servers';
import { getMcpApiErrorMessage } from '@terra/shared-contracts-mcp-servers';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';
import { buildMcpOAuthAuthorizeQuery } from '../../utils/mcpOAuthAuthorizeQuery';

const MCP_SERVERS_QUERY_KEY = 'mcp-servers';

function buildClientOptions() {
  return {
    baseUrl: `${getCurrentGatewayUrl()}/ai`,
  };
}

async function withAuthHeaders() {
  return getAuthHeaders();
}

export function useMcpServers() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [MCP_SERVERS_QUERY_KEY, 'user'],
    queryFn: async () => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.listMcpServers({
        query: { scope: 'USER' },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 200) return response.body.servers;
      return [];
    },
    staleTime: 30_000,
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
  }, [queryClient]);

  return {
    servers: query.data ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
    invalidate,
  };
}

export function useCreateMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMcpServerDto) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.createMcpServer({
        body: data,
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200)
        throw new Error(
          getMcpApiErrorMessage(response.body, 'Failed to create MCP server'),
        );
      return response.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useUpdateMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateMcpServerDto;
    }) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.updateMcpServer({
        params: { id },
        body: data,
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200)
        throw new Error(
          getMcpApiErrorMessage(response.body, 'Failed to update MCP server'),
        );
      return response.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useDeleteMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.deleteMcpServer({
        params: { id },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 404) throw new Error('MCP server not found');
      if (response.status !== 200)
        throw new Error('Failed to delete MCP server');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useUpsertMcpCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      credential,
    }: {
      id: string;
      credential: UpsertMcpCredentialDto;
    }) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.upsertMcpCredential({
        params: { id },
        body: credential,
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 404) throw new Error('MCP server not found');
      if (response.status !== 200) throw new Error('Failed to save credential');
      return response.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useAuthorizeMcpServer() {
  return useMutation({
    mutationFn: async (id: string) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.oauthAuthorize({
        params: { id },
        query: await buildMcpOAuthAuthorizeQuery(),
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200)
        throw new Error(
          getMcpApiErrorMessage(
            response.body,
            'Failed to authorize MCP server',
          ),
        );
      return response.body.authorizeUrl;
    },
  });
}

export function useCompleteMcpOAuthCallback() {
  return useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      const authHeaders = await withAuthHeaders();
      const callbackUrl = new URL(
        `${getCurrentGatewayUrl().replace(/\/$/, '')}/ai/mcp-servers/oauth/callback`,
      );
      callbackUrl.searchParams.set('code', code);
      callbackUrl.searchParams.set('state', state);

      const response = await fetch(callbackUrl.toString(), {
        method: 'GET',
        headers: authHeaders,
        redirect: 'manual',
      });

      if (response.status >= 400) {
        throw new Error('Failed to complete MCP OAuth authorization');
      }
    },
  });
}

export function useTestMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.testMcpServer({
        params: { id },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200) throw new Error('Failed to test connection');
      return response.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useApplicationMcpServers({
  applicationId,
}: {
  applicationId: string;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [MCP_SERVERS_QUERY_KEY, 'application', applicationId],
    queryFn: async () => {
      if (!applicationId) return [];
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.listApplicationMcpServers({
        params: { applicationId },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 200) return response.body.servers;
      return [];
    },
    enabled: !!applicationId,
    staleTime: 30_000,
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [MCP_SERVERS_QUERY_KEY, 'application', applicationId],
    });
  }, [queryClient, applicationId]);

  return {
    servers: query.data ?? [],
    isLoading: query.isLoading,
    invalidate,
  };
}

export function useAttachMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      mcpServerId,
      enabled,
    }: {
      applicationId: string;
      mcpServerId: string;
      enabled: boolean;
    }) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.attachMcpServerToApplication({
        params: { applicationId, mcpServerId },
        body: { enabled },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200)
        throw new Error('Failed to attach MCP server');
      return response.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useDetachMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      mcpServerId,
    }: {
      applicationId: string;
      mcpServerId: string;
    }) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.detachMcpServerFromApplication({
        params: { applicationId, mcpServerId },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200)
        throw new Error('Failed to detach MCP server');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useTenantMcpServers() {
  const query = useQuery({
    queryKey: [MCP_SERVERS_QUERY_KEY, 'tenant'],
    queryFn: async () => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.listMcpServers({
        query: { scope: 'TENANT' },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 200) return response.body.servers;
      return [];
    },
    staleTime: 30_000,
  });

  return {
    servers: query.data ?? [],
    isLoading: query.isLoading,
  };
}

export function useCopilotAvailableMcpServers() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [MCP_SERVERS_QUERY_KEY, 'copilot-available'],
    queryFn: async () => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.listCopilotAvailableMcpServers({
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 200) return response.body.servers;
      return [];
    },
    staleTime: 30_000,
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [MCP_SERVERS_QUERY_KEY, 'copilot-available'],
    });
  }, [queryClient]);

  return {
    servers: query.data ?? [],
    isLoading: query.isLoading,
    invalidate,
  };
}

export function useOptInToCopilotMcp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mcpServerId: string) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.optInToCopilotMcp({
        params: { mcpServerId },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200)
        throw new Error('Failed to opt in to MCP server');
      return response.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useOptOutFromCopilotMcp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mcpServerId: string) => {
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.optOutFromCopilotMcp({
        params: { mcpServerId },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200)
        throw new Error('Failed to opt out from MCP server');
      return response.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_QUERY_KEY] });
    },
  });
}

export function useTenantMcpServer({ id }: { id: string }) {
  const query = useQuery({
    queryKey: [MCP_SERVERS_QUERY_KEY, 'tenant', id],
    queryFn: async () => {
      if (!id) return null;
      const authHeaders = await withAuthHeaders();
      const response = await mcpServersClient.getMcpServer({
        params: { id },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 200) return response.body;
      return null;
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });

  return {
    server: query.data ?? undefined,
    isLoading: query.isLoading,
  };
}

export const useUpsertTenantMcpCredential = useUpsertMcpCredential;
export const useAuthorizeTenantMcpServer = useAuthorizeMcpServer;
